import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { pageWindow } from '../paging'
import { t } from '../strings'
import { Icon } from './Icon'

/** The top of every page: where you are, what it is for, and the page's own actions. */
export function PageHeader({ title, subtitle, back, children }: { title: string; subtitle?: string; back?: { to: string; label?: string }; children?: ReactNode }) {
  return (
    <header className="page-header">
      <div className="page-header-text">
        {back && (
          <Link to={back.to} className="back-link">
            <Icon name="chevronLeft" size={18} />
            {back.label ?? t.back}
          </Link>
        )}
        <h1>{title}</h1>
        {subtitle && <p className="muted page-sub">{subtitle}</p>}
      </div>
      {children && <div className="page-actions">{children}</div>}
    </header>
  )
}

/** A big, plain number with its name: for the figures an owner looks at first. */
export function Stat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: 'primary' }) {
  return (
    <div className={tone === 'primary' ? 'stat stat-primary' : 'stat'}>
      <div className="stat-label">{label}</div>
      <div className="stat-value big">{value}</div>
      {note && <div className="stat-note">{note}</div>}
    </div>
  )
}

/** A link that looks like a card, with an icon, a title and one line saying what is behind it. */
export function LinkCard({ to, icon, title, hint, tone }: { to: string; icon: Parameters<typeof Icon>[0]['name']; title: string; hint: string; tone?: 'danger' }) {
  return (
    <Link to={to} className={tone === 'danger' ? 'link-card is-danger' : 'link-card'}>
      <span className="link-card-icon">
        <Icon name={icon} />
      </span>
      <span className="link-card-text">
        <span className="link-card-title">{title}</span>
        <span className="muted small">{hint}</span>
      </span>
      <Icon name="chevronRight" size={20} />
    </Link>
  )
}

/** Previous / next and page numbers, with "Menampilkan 16-30 dari 120". `page` starts at 1. */
export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  if (total === 0) return null
  const from = (page - 1) * pageSize + 1
  const to = Math.min(total, page * pageSize)
  return (
    <nav className="pagination" aria-label={t.pagination}>
      <span className="muted small pagination-count">{t.showing(from, to, total)}</span>
      <div className="pagination-controls">
        <button type="button" className="secondary compact" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <Icon name="chevronLeft" size={18} />
          {t.previous}
        </button>
        <ul className="pagination-pages">
          {pageWindow(page, pages).map((n, i) =>
            n === 'gap' ? (
              <li key={`gap-${i}`} aria-hidden="true" className="muted">
                …
              </li>
            ) : (
              <li key={n}>
                <button
                  type="button"
                  className={n === page ? 'page-number on' : 'page-number'}
                  aria-label={t.pageNumber(n)}
                  aria-current={n === page ? 'page' : undefined}
                  onClick={() => onPage(n)}
                >
                  {n}
                </button>
              </li>
            ),
          )}
        </ul>
        <button type="button" className="secondary compact" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          {t.next}
          <Icon name="chevronRight" size={18} />
        </button>
      </div>
    </nav>
  )
}
